import { default as React, useEffect, useMemo, useState } from 'react'

import { type AmplitudeInstance, getAmplitudeClient } from './core/amplitude'
import { AmplitudeContext } from './core/amplitude-context'
import type { AmplitudeConfig } from './core/config'

export type TrackingStrategy = 'dnt-local-storage' | 'always'

interface Props {
  /** Nothing is initialised while `undefined` (no api key, opted out, or not known yet); it may arrive after mount. */
  config?: AmplitudeConfig
  /** `dnt-local-storage` (default) only tracks after the user consented; `always` for B2B. */
  trackingStrategy?: TrackingStrategy
}

function isTrackingEnabled(strategy: TrackingStrategy) {
  if (strategy === 'always') {
    return true
  }

  return localStorage.getItem('dnt') === '0'
}

/** Mount it once, at the root of the app: the client is one per api key and the first configuration wins. */
const AmplitudeProvider = ({
  config,
  children,
  trackingStrategy = 'dnt-local-storage',
}: React.PropsWithChildren<Props>) => {
  const [instance, setInstance] = useState<AmplitudeInstance | null>(null)

  const apiKey = config?.apiKey
  useEffect(() => {
    if (!config || !apiKey || instance) return

    if (isTrackingEnabled(trackingStrategy)) {
      setInstance(getAmplitudeClient(config))
    }
  }, [apiKey])

  const value = useMemo(() => ({ amplitude: instance?.client ?? null, instance }), [instance])

  return <AmplitudeContext.Provider value={value}>{children}</AmplitudeContext.Provider>
}

export default AmplitudeProvider
